import gql from 'graphql-tag';

import { Mutation, Query, QueryRegistry } from 'core/index';

import { PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';
import { STORY_CONNECTION_FIELDS } from 'apps/story/gql';
import { COLLECTION_CONNECTION_FIELDS } from 'apps/collection/gql';
import {
  INITIATIVE_FIELDS,
  INITIATIVE_CONNECTION_FIELDS,
} from 'apps/initiative/gql/fragments';

import { CONTENT_FIELDS } from 'apps/content/gql';
import { LOCAL_PROFILE_FIELDS } from 'apps/signIn/gql';

export const PROFILE_LIST_FIELDS = gql`
  fragment profileListFields on Profile {
    id
    username
    slug
    bio
    fullName
    avatar {
      id
      filename
      path
      url
      completed
    }
    imageFallbackChar
    title
    followingCount
    content {
      id
      isFollowed
      whitelabel
      creator {
        id
        username
      }
    }
  }
`;

export const UPDATE_SPACE_FLAG = gql`
  mutation updateSpaceFlag($input: UpdateSpaceFlagsInput) {
    updateSpaceFlags(input: $input) {
      updateSpaceFlags {
        id
        spaceType
        flags
      }
    }
  }
`;

export const PROFILE_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment profileFields on Profile {
    id
    canUpdate
    canDelete
    fullName
    preferredName
    bio
    avatar {
      id
      path
      url
      completed
    }
    imageFallbackChar
    email
    followingCount
    content {
      ...contentFields
    }
    inSpace {
      id
      whichTypes
      trialPeriodEnd
      spaceType
      content {
        id
        slug
      }
    }
    user {
      id
      username
      email
    }
    socialAccounts {
      id
      accountType
      accountName
    }
    extraMetadata
    skills {
      id
      name
    }
    initiatives {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
      edges {
        node {
          id
          title
          description
          slug
          imageFallbackChar
          content {
            ...contentFields
          }
          avatar {
            id
            path
            url
          }
        }
      }
    }
  }
`;

export const PROFILE_CONNECTION_FIELDS = gql`
  ${PROFILE_LIST_FIELDS}
  fragment profileConnectionFields on ProfileConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        ...profileListFields
      }
    }
  }
`;

export const FOLLOWING_PROJECT_CONNECTION_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment followingProjectConnectionFields on ProjectConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        id
        image {
          id
          path
          url
          completed
        }
        slug
        descriptionSnippet
        content {
          ...contentFields
        }
        creatorProfile {
          id
          username
        }
        space {
          id
          spaceType
          content {
            id
            slug
          }
        }
      }
    }
  }
`;

export const SIGNUP_MUTATION = gql`
  ${LOCAL_PROFILE_FIELDS}
  mutation Signup($signupInput: SignupInput) {
    signupResult(signupData: $signupInput)
      @connection(key: "signupResult", filter: [""]) {
      result
      token
      refreshToken
      profile {
        ...localProfileFields
      }
    }
  }
`;

export const UPDATE_PROMOTION = gql`
  mutation UpdatePromotion($updatePromotionData: UpdatePromotionInput) {
    updatePromotion(updatePromotionData: $updatePromotionData) {
      updatePromotion {
        id
        freeSeats
        discount
        earlyAccess
        space {
          id
        }
      }
    }
  }
`;

export class UpdateSpaceFlagMutation extends Mutation {
  static conf = {
    GQL: UPDATE_SPACE_FLAG,
    inputName: 'input',
    resultPath: 'updateSpaceFlags.updateSpaceFlags',
  };
}

export class UpdatePromotionMutation extends Mutation {
  static conf = {
    GQL: UPDATE_PROMOTION,
    inputName: 'updatePromotionData',
    resultPath: 'updatePromotion.updatePromotion',
  };
}

export class SignUpMutation extends Mutation {
  static conf = {
    GQL: SIGNUP_MUTATION,
    inputName: 'signupInput',
    resultPath: 'signupResult',
  };
}

export const PROFILE_THREAD_CONNECTION_FIELDS = gql`
  fragment profileThreadConnectionFields on ThreadConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        id
        title
        forum {
          id
          parentContent {
            id
            slug
          }
        }
        category {
          id
          title
          color
          urlId
        }
        commentsCount
        dateCreated
        lastActivityAt
        creatorProfile {
          id
          username
          fullName
          imageFallbackChar
          avatar {
            id
            path
            url
          }
        }
        urlId
        content {
          id
          isLiked
          likesCount
          creator {
            id
            username
          }
        }
      }
    }
  }
`;

const SIGNUP_ORGANIZATION = gql`
  ${INITIATIVE_FIELDS}
  ${PROFILE_FIELDS}
  mutation SignupOrganization(
    $initiativeData: InitiativeInput
    $profileData: SignupInput
  ) {
    initiativeAndProfile(
      initiativeData: $initiativeData
      profileData: $profileData
    ) @connection(key: "initiativeAndProfile", filter: [""]) {
      profile {
        ...profileFields
      }
      initiative {
        ...initiativeFields
      }
    }
  }
`;

export class SignUpOrganizationMutation extends Mutation {
  static conf = {
    GQL: SIGNUP_ORGANIZATION,
    inputName: ['initiativeData', 'profileData'],
    resultPath: 'initiativeAndProfile',
  };
}

export const PROFILE_MUTATION = gql`
  ${PROFILE_FIELDS}
  ${LOCAL_PROFILE_FIELDS}
  mutation Profile($profileInput: ProfileInput) {
    profile(profileData: $profileInput) {
      profile {
        ...profileFields
        ...localProfileFields
      }
    }
  }
`;
export class ProfileMutation extends Mutation {
  static conf = {
    GQL: PROFILE_MUTATION,
    inputName: 'profileInput',
    resultPath: 'profile.profile',
  };
}

const CONFIRM_PROFILE_MUTATION = gql`
  ${LOCAL_PROFILE_FIELDS}
  mutation ConfirmProfile($confirmProfileInput: ConfirmProfileInput) {
    confirmProfile(profileData: $confirmProfileInput) {
      profile {
        ...localProfileFields
      }
    }
  }
`;

export class ConfirmProfileMutation extends Mutation {
  static conf = {
    GQL: CONFIRM_PROFILE_MUTATION,
    inputName: 'confirmProfileInput',
    resultPath: 'confirmProfile.profile',
  };
}

// NOTE:
// {username:"mkampik", fullName:"Max K", description:"blah balh"}
// Alternatively to defining multiple queries... pass extra parameters to turn on and off each
// section of the profile using the @skip/@include directives
// This would require many extra parameters
export const PROFILE = gql`
  ${PROFILE_FIELDS}
  ${PROJECT_CONNECTION_FIELDS}
  ${STORY_CONNECTION_FIELDS}
  ${COLLECTION_CONNECTION_FIELDS}
  ${PROFILE_CONNECTION_FIELDS}
  ${PROFILE_THREAD_CONNECTION_FIELDS}
  ${INITIATIVE_CONNECTION_FIELDS}
  ${FOLLOWING_PROJECT_CONNECTION_FIELDS}
  query Profile(
    $username: String!
    $projectsCursor: String
    $projectsCount: Int
    $projectsSortBy: String
    $storiesCursor: String
    $storiesCount: Int
    $storiesSortBy: String
    $collectionsCount: Int
    $collectionsCursor: String
    $collectionsSortBy: String
    $threadBatchSize: Int
    $threadCursor: String
    $threadSortBy: String
    $followersCount: Int
    $followersCursor: String
    $followersSortBy: String
    $followingProfilesCount: Int
    $followingProfilesCursor: String
    $followingProfilesSortBy: String
    $followingInitiativesCount: Int
    $followingInitiativesCursor: String
    $followingInitiativesSortBy: String
    $followingProjectsCount: Int
    $followingProjectsCursor: String
    $followingProjectsSortBy: String
  ) {
    profile(username: $username) {
      ...profileFields
      inSpace {
        flags
      }
      management
      contributedProjects(
        first: $projectsCount
        after: $projectsCursor
        sortBy: $projectsSortBy
      ) {
        ...projectConnectionFields
      }
      stories(
        first: $storiesCount
        after: $storiesCursor
        sortBy: $storiesSortBy
      ) {
        ...storyConnectionFields
      }
      collections(
        first: $collectionsCount
        after: $collectionsCursor
        sortBy: $collectionsSortBy
      ) {
        ...collectionConnectionFields
      }
      threads(
        first: $threadBatchSize
        after: $threadCursor
        sortBy: $threadSortBy
      ) {
        ...profileThreadConnectionFields
      }
      followers(
        first: $followersCount
        after: $followersCursor
        sortBy: $followersSortBy
      ) {
        ...profileConnectionFields
      }
      followingProfiles(
        first: $followingProfilesCount
        after: $followingProfilesCursor
        sortBy: $followingProfilesSortBy
      ) {
        ...profileConnectionFields
      }
      followingInitiatives(
        first: $followingInitiativesCount
        after: $followingInitiativesCursor
        sortBy: $followingInitiativesSortBy
      ) {
        ...initiativeConnectionFields
      }
      followingProjects(
        first: $followingProjectsCount
        after: $followingProjectsCursor
        sortBy: $followingProjectsSortBy
      ) {
        ...followingProjectConnectionFields
      }
    }
  }
`;
export class ProfileQuery extends Query {
  static conf = {
    GQL: PROFILE,
    varNames: [
      'username',
      'projectsCursor',
      'projectsSortBy',
      'projectsCount',
      'storiesCursor',
      'storiesCount',
      'storiesSortBy',
      'collectionsCursor',
      'collectionsSortBy',
      'collectionsCount',
      'threadBatchSize',
      'threadCursor',
      'threadSortBy',
      'followersCount',
      'followersCursor',
      'followersSortBy',
      'followingProfilesCount',
      'followingProfilesCursor',
      'followingProfilesSortBy',
      'followingInitiativesCount',
      'followingInitiativesCursor',
      'followingInitiativesSortBy',
      'followingProjectsCursor',
      'followingProjectsCount',
      'followingProjectsSortBy',
    ],
    resultPath: 'profile',
  };
}

const PROFILES = gql`
  ${CONTENT_FIELDS}
  query Profiles($batchSize: Int, $cursor: String, $sortBy: String) {
    profiles(first: $batchSize, after: $cursor, sortBy: $sortBy) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
      edges {
        cursor
        node {
          id
          fullName
          username
          imageFallbackChar
          avatar {
            id
            path
            url
            completed
          }
          bio
          content {
            ...contentFields
          }
        }
      }
    }
  }
`;

export class ProfilesQuery extends Query {
  static conf = {
    GQL: PROFILES,
    varNames: ['batchSize', 'cursor', 'sortBy'],
    resultPath: 'profiles',
  };
}

const COMMUNITY_PROFILES = gql`
  query CommunityProfiles($batchSize: Int, $cursor: String, $sortBy: String) {
    homeProfiles(first: $batchSize, after: $cursor, sortBy: $sortBy) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
      edges {
        cursor
        node {
          id
          fullName
          username
          imageFallbackChar
          avatar {
            id
            path
            url
            completed
          }
          bio
          content {
            id
          }
        }
      }
    }
  }
`;

export class CommunityProfilesQuery extends Query {
  static conf = {
    GQL: COMMUNITY_PROFILES,
    varNames: ['batchSize', 'cursor', 'sortBy'],
    resultPath: 'homeProfiles',
  };
}

export const AUTOCOMPLETE_PROFILES = gql`
  query ProfilesComplete($complete: String, $spaceId: ID) {
    profilesComplete(complete: $complete, spaceId: $spaceId) {
      id
      slug
      username
      fullName
      avatar {
        id
        path
        url
      }
      imageFallbackChar
    }
  }
`;

export const AUTOCOMPLETE_PROFILES_CONTAIN = gql`
  query ProfilesCompleteContain($complete: String, $first: Int) {
    profiles(first: $first, contains: ["slug", $complete]) {
      edges {
        node {
          id
          slug
          username
          fullName
          avatar {
            id
            path
            url
          }
          imageFallbackChar
        }
      }
    }
  }
`;

export class CompleteProfilesQuery extends Query {
  static conf = {
    GQL: AUTOCOMPLETE_PROFILES,
    varNames: ['complete', 'spaceId'],
    resultPath: 'profilesComplete',
  };
}

export class AutocompleteProfilesQuery extends Query {
  static conf = {
    GQL: AUTOCOMPLETE_PROFILES_CONTAIN,
    varNames: ['complete', 'first'],
    resultPath: 'profiles.edges',
  };
}

QueryRegistry.add('profile.CompleteProfilesQuery', CompleteProfilesQuery);

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($changePasswordInput: ChangePasswordInput) {
    changePassword(changePasswordInput: $changePasswordInput)
      @connection(key: "changePassword", filter: [""]) {
      result
      msg
    }
  }
`;

const EMAIL_PREFERENCES = gql`
  query EmailPreferences {
    loggedInUser {
      id
      emailsPreferences {
        id
        displayName
        description
        enabled
      }
    }
  }
`;

export class EmailPreferencesQuery extends Query {
  static conf = {
    GQL: EMAIL_PREFERENCES,
    resultPath: 'loggedInUser.emailsPreferences',
  };
}

const FOLLOWING_PROFILES_CONNECTION = gql`
  ${PROFILE_CONNECTION_FIELDS}
  query FollowingProfiles(
    $username: String!
    $followingProfilesCount: Int
    $followingProfilesCursor: String
    $followingProfilesSortBy: String
  ) {
    profile(username: $username) {
      id
      followingProfiles(
        first: $followingProfilesCount
        after: $followingProfilesCursor
        sortBy: $followingProfilesSortBy
      ) {
        ...profileConnectionFields
      }
    }
  }
`;

export class FollowingProfilesConnectionQuery extends Query {
  static conf = {
    GQL: FOLLOWING_PROFILES_CONNECTION,
    resultPath: 'profile.followingProfiles',
    varNames: [
      'username',
      'followingProfilesCount',
      'followingProfilesCursor',
      'followingProfilesSortBy',
    ],
  };
}

const FOLLOWING_ORGANIZATIONS_CONNECTION = gql`
  ${INITIATIVE_CONNECTION_FIELDS}
  query FollowingOrganizations(
    $username: String!
    $followingInitiativesCount: Int
    $followingInitiativesCursor: String
    $followingInitiativesSortBy: String
  ) {
    profile(username: $username) {
      id
      followingInitiatives(
        first: $followingInitiativesCount
        after: $followingInitiativesCursor
        sortBy: $followingInitiativesSortBy
      ) {
        ...initiativeConnectionFields
      }
    }
  }
`;

export class FollowingOrganizationsConnectionQuery extends Query {
  static conf = {
    GQL: FOLLOWING_ORGANIZATIONS_CONNECTION,
    resultPath: 'profile.followingInitiatives',
    varNames: [
      'username',
      'followingInitiativesCount',
      'followingInitiativesCursor',
      'followingInitiativesSortBy',
    ],
  };
}

const FOLLOWING_PROJECTS_CONNECTION = gql`
  ${FOLLOWING_PROJECT_CONNECTION_FIELDS}
  query FollowingProjects(
    $username: String!
    $followingProjectsCount: Int
    $followingProjectsCursor: String
    $followingProjectsSortBy: String
  ) {
    profile(username: $username) {
      id
      followingProjects(
        first: $followingProjectsCount
        after: $followingProjectsCursor
        sortBy: $followingProjectsSortBy
      ) {
        ...followingProjectConnectionFields
      }
    }
  }
`;

export class FollowingProjectsConnectionQuery extends Query {
  static conf = {
    GQL: FOLLOWING_PROJECTS_CONNECTION,
    resultPath: 'profile.followingProjects',
    varNames: [
      'username',
      'followingProjectsCount',
      'followingProjectsCursor',
      'followingProjectsSortBy',
    ],
  };
}

const PROFILE_FOLLOWING_COUNT = gql`
  query Profile($username: String!) {
    profile(username: $username) {
      id
      followingCount
    }
  }
`;

export class ProfileFollowingCountQuery extends Query {
  static conf = {
    GQL: PROFILE_FOLLOWING_COUNT,
    varNames: ['username'],
    resultPath: 'profile',
  };
}

const LANGUAGE_PREFERENCES = gql`
  query ProfileLanguage($username: String!) {
    profile(username: $username) {
      id
      user {
        id
        locale
      }
    }
  }
`;

export class LanguagePreferencesQuery extends Query {
  static conf = {
    GQL: LANGUAGE_PREFERENCES,
    varNames: ['username'],
    resultPath: 'profile',
  };
}

const CHANGE_EMAIL_PREFERENCES = gql`
  mutation ChangeEmailPreferences(
    $changeEmailPreferencesInput: [ChangeEmailPreferencesInput]
  ) {
    changeEmailPreferences(
      changeEmailPreferencesInput: $changeEmailPreferencesInput
    ) {
      result
      msg
    }
  }
`;

const CHANGE_LANGUAGE_PREFERENCES = gql`
  mutation ChangeLanguagePreferences(
    $changeLanguagePreferencesInput: ChangeLanguagePreferencesInput
  ) {
    changeLanguagePreferences(
      changeLanguagePreferencesInput: $changeLanguagePreferencesInput
    ) {
      result
      msg
    }
  }
`;

export class ChangePasswordMutation extends Mutation {
  static conf = {
    GQL: CHANGE_PASSWORD,
    inputName: 'changePasswordInput',
    resultPath: 'changePassword',
  };
}

export class ChangeEmailPreferencesMutation extends Mutation {
  static conf = {
    GQL: CHANGE_EMAIL_PREFERENCES,
    inputName: 'changeEmailPreferencesInput',
    resultPath: 'changeEmailPreferences',
  };
}

export class ChangeLanguagePreferencesMutation extends Mutation {
  static conf = {
    GQL: CHANGE_LANGUAGE_PREFERENCES,
    inputName: 'changeLanguagePreferencesInput',
    resultPath: 'changeLanguagePreferences',
  };
}

const PROFILE_CHECK = gql`
  query Profile($username: String!) {
    profile(username: $username) {
      id
      canUpdate
    }
  }
`;

export class ProfileCheckQuery extends Query {
  static conf = {
    GQL: PROFILE_CHECK,
    varNames: ['username'],
    resultPath: 'profile',
  };
}

const PROFILE_INTENT = gql`
  query ProfileIntents {
    profileIntents {
      id
      name
    }
  }
`;

const BILLING_SUBSCRIPTION = gql`
  query Profile($username: String!) {
    profile(username: $username) {
      id
      promotion {
        id
        spaceId
        freeSeats
        discount
        earlyAccess
      }
    }
  }
`;

export class BillingSubscriptionQuery extends Query {
  static conf = {
    GQL: BILLING_SUBSCRIPTION,
    varNames: ['username'],
    resultPath: 'profile',
  };
}

export class ProfileIntentQuery extends Query {
  static conf = {
    GQL: PROFILE_INTENT,
    resultPath: 'profileIntents',
  };
}

QueryRegistry.add('profile.ProfileIntentsQuery', ProfileIntentQuery);

const PROFILE_TYPES = gql`
  query ProfileTypes {
    profileUserTypes {
      id
      name
    }
  }
`;

export class ProfileTypesQuery extends Query {
  static conf = {
    GQL: PROFILE_TYPES,
    resultPath: 'profileUserTypes',
  };
}

QueryRegistry.add('profile.ProfileTypesQuery', ProfileTypesQuery);

const BAN_PROFILE = gql`
  mutation Ban($banData: BanProfileInput) {
    banProfile(banData: $banData) {
      result
      management
    }
  }
`;

export class BanProfileMutation extends Mutation {
  static conf = {
    GQL: BAN_PROFILE,
    inputName: 'banData',
    resultPath: 'banProfile',
  };
}

const DELETE_PROFILE = gql`
  mutation DeleteProfile($userData: DeleteProfileInput) {
    deleteProfile(userData: $userData) {
      result
    }
  }
`;

export class DeleteProfileMutation extends Mutation {
  static conf = {
    GQL: DELETE_PROFILE,
    inputName: 'userData',
    resultPath: 'deleteProfile',
  };
}
